import type { Observable } from 'rxjs';
import { filter, from, map } from 'rxjs';

import {
  IngredientType,
  IngredientTypeQL,
} from '@libs/trip-planner-interfaces';
import type {
  AvailableFlightIngredientResponse,
  AvailableFerryIngredientResponse,
  TransportAvailabilityCriteria,
  TransportListResponse,
  TransportListResponseQL,
  ActivityAvailabilityCriteria,
  TicketResponseQL,
  TransportListRequestQL,
  CheapestHotelStayRequestQL,
  AccommodationAvailabilityCriteria,
  AccommodationAvailabilityFetchResult,
  HotelStayResponseQL,
  HotelBookableOptionRoomQL,
  HotelBookableOptionRoom,
  RoomPax,
  HotelBookableOptionWithRoomsAndDate,
} from '@libs/trip-planner-interfaces';
import { assertValue, isDefined } from '@libs/utils';
import { isFeatureToggleActive } from '@libs/feature-toggle';

import { getApolloClient } from './infrastructure';
import {
  GET_TRANSPORT_AVAILABILITY_QUERY,
  SEARCH_PRODUCT_AVAILABILITY_SUBSCRIPTION,
  SEARCH_TICKET_RATES_QUERY,
} from './queries';
import { logged, zenToRx } from './rx';
import {
  getAccommodationProductAvailabilityRequestQL,
  getTicketRequestQL,
  getTransportListRequestQL,
  normalizeDate,
} from './utils';
import {
  toFlightIngredientResponse,
  toFerryIngredientResponse,
} from './mappers';
import { isAvailableTransportIngredientResponse } from './typeGuards';

export class ProductAvailabilitySearchService {
  public searchAccommodationProductAvailability(
    criteria: AccommodationAvailabilityCriteria
  ): Observable<HotelBookableOptionWithRoomsAndDate> {
    const request: CheapestHotelStayRequestQL =
      getAccommodationProductAvailabilityRequestQL(criteria);

    const productAvailabilityResponse =
      zenToRx<AccommodationAvailabilityFetchResult>(
        getApolloClient().subscribe({
          variables: {
            request,
          },
          query: SEARCH_PRODUCT_AVAILABILITY_SUBSCRIPTION,
        })
      );

    return productAvailabilityResponse.pipe(
      logged('Product Availability Response'),
      map((fetchResult) => fetchResult.data?.searchCheapestStay),
      filter(isDefined),
      map<HotelStayResponseQL, HotelBookableOptionWithRoomsAndDate>(
        (responseQL) => ({
          date: new Date(responseQL.date),
          addToBasketToken: responseQL.addToBasketToken,
          code: responseQL.hotelCode,
          isRefundable: responseQL.isRefundable,
          rooms: responseQL.rooms.map(this.toHotelBookableOptionRoom),
          totalPrice: responseQL.totalPrice,
        })
      )
    );
  }

  public searchActivityProductAvailability(
    criteria: ActivityAvailabilityCriteria
  ): Observable<TicketResponseQL> {
    const request = getTicketRequestQL(criteria);
    const response = getApolloClient().query<TicketResponseQL>({
      query: SEARCH_TICKET_RATES_QUERY,
      variables: {
        request,
      },
    });
    return from(response).pipe(map((queryResult) => queryResult.data));
  }

  public searchTransportProductAvailability(
    criteria: TransportAvailabilityCriteria
  ): Observable<TransportListResponse> {
    const ingredientsList = criteria.userCriteria.recipe.ingredients;

    // FYI: This is to shift date by timezone offset
    criteria.userCriteria.day = normalizeDate(criteria.userCriteria.day);

    if (
      ingredientsList === null ||
      ingredientsList.length === 0 ||
      ingredientsList[criteria.userCriteria.ingredientIndex] === undefined
    ) {
      return from([]);
    }

    const request: TransportListRequestQL = getTransportListRequestQL(criteria);

    request.recipe.ingredients.forEach((ingredient, index) => {
      if (
        ingredient.ingredientType === IngredientTypeQL.Transport &&
        index === criteria.userCriteria.ingredientIndex
      ) {
        ingredient.flightNumbers = [];
      }
    });

    const response = getApolloClient().query<{
      getTransportList: TransportListResponseQL;
    }>({
      query: GET_TRANSPORT_AVAILABILITY_QUERY,
      variables: {
        request,
      },
    });

    return from(response).pipe(
      map((queryResult) => queryResult.data.getTransportList),
      map<TransportListResponseQL, TransportListResponse>((responseQL) => ({
        totalTransports: responseQL.totalTransports,
        transports: responseQL.transports.reduce(
          (
            list: (
              | AvailableFlightIngredientResponse
              | AvailableFerryIngredientResponse
            )[],
            transportListResult
          ) => {
            const ingredient =
              ingredientsList[criteria.userCriteria.ingredientIndex];

            assertValue(
              ingredient,
              'search-service-smart-search > productAvailabilitySearchService.ts > searchTransportProductAvailability > ingredient'
            );

            if (ingredient.ingredientType === IngredientType.Flight) {
              const response = toFlightIngredientResponse(
                transportListResult,
                ingredient
              );

              if (isAvailableTransportIngredientResponse(response)) {
                list.push(response);
              }
            } else if (ingredient.ingredientType === IngredientType.Ferry) {
              const response = toFerryIngredientResponse(
                transportListResult,
                ingredient
              );

              if (isAvailableTransportIngredientResponse(response)) {
                list.push(response);
              }
            } else {
              throw new Error(
                'Impossible code path reached! TransportListResult should be mapped to transport in recipe'
              );
            }

            return list;
          },
          []
        ),
      }))
    );
  }

  private toHotelBookableOptionRoom(
    roomQL: HotelBookableOptionRoomQL
  ): HotelBookableOptionRoom {
    const childAges = roomQL.pax
      .map((p) => p.age)
      .filter<number>((age): age is number => age !== null);

    const numberOfAdults = roomQL.pax.length - childAges.length;

    const pax: RoomPax = {
      numberOfAdults,
      childAges,
    };
    return {
      ...roomQL,
      pax,
      roomTypeId: isFeatureToggleActive('room-types')
        ? roomQL.roomTypeId
        : undefined,
    };
  }
}
